import axios from "axios";
import AuthService from '../services/AuthService'
import store from '../store';

const MAX_RETRY = 3;

const createAxiosInstance = (baseURL) => {
  const instance = axios.create({ baseURL });

  // Request Interceptor
  instance.interceptors.request.use(
    (config) => {
      const token = AuthService.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response Interceptor
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      notifyNetworkError(error);
      const originalRequest = error.config;
      if (error.response && error.response.status === 401) {
        // Track retries on the request config to persist across interceptor calls
        originalRequest._retryCount = originalRequest._retryCount || 0;
        
        if (originalRequest._retryCount >= MAX_RETRY) {
          await handleAuthFailure();
          return Promise.reject(error);
        }
        
        originalRequest._retryCount += 1;

        let retryCount = 1;
        while(retryCount <= MAX_RETRY) {
          try {
            if (error.config.url === "/verify-refresh-token") {
                window.dispatchEvent(new CustomEvent("app-auth-failure"));
                return Promise.reject(error);
            }
            const currentRefreshToken = store.getters.refreshToken;
  
            const response = await AuthService.verifyRefreshToken(currentRefreshToken);
            const { accessToken } = response.data;
            // Update token in store
            store.dispatch("setToken", accessToken);
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return instance(originalRequest);
          }
          catch (refreshError) {
            if (retryCount < MAX_RETRY) {
              const delay = getBackoffDelay(retryCount);
              retryCount += 1;
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
            await handleAuthFailure();
            return Promise.reject(refreshError);
          }
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const notifyNetworkError = (error) => {
  if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
  if (
    typeof window !== "undefined" &&
    (!error.response || error.code === "ERR_NETWORK" || error.message === "Network Error")
  ) {
    window.dispatchEvent(new CustomEvent("app-network-error"));
  }
};

const getBackoffDelay = (attempt) => attempt * 5 * 1000; // 5s, 10s, 15s

const handleAuthFailure = async () => {
    const userType = store.getters.userType;
    const cachedUrl = store.getters.sourceUrl;
    if (cachedUrl) {
      window.location.replace(cachedUrl);
    }
    else if (userType) {
      // other external user types
      window.close();
    }
    else {
      // internal user
      AuthService.logout();
      window.location.href = '/#/login';
    }
}

  export default createAxiosInstance;
  